import { useRef } from "react"
import useEmblaCarousel from "embla-carousel-react"

import {
  freeMinutesFromUsedCredits,
  hasStartCreditsLeft,
  useChat,
} from "@/features/chat"
import { useLogger, ANALYTICS_EVENTS } from "@/features/analytics"

import { advisorsList } from "../config/advisors"
import { sectionCopy } from "../config/sections"
import type { Advisor } from "../types"

import Dot from "@/assets/svg/advisors/Dot.svg?react"
import DiscIcon from "@/assets/img/live-chat/discount_icon.svg?react"
import OfferDecor from "@/assets/img/live-chat/free_offer_decor.webp"

const DRAG_THRESHOLD = 5

interface AllAdvisorsProps {
  onClick?: (advisor: Advisor) => void
}

export function AllAdvisors({ onClick }: AllAdvisorsProps) {
  const [emblaRef] = useEmblaCarousel({ align: "start", containScroll: "trimSnaps" })
  const { user, hasPurchasedCredits, userUsedCredits } = useChat()
  const { trackAppyCntEvent } = useLogger()

  const start = useRef({ x: 0, y: 0 })
  const dragged = useRef(false)

  const handlePointerDown = (e: React.PointerEvent) => {
    dragged.current = false
    start.current = { x: e.clientX, y: e.clientY }
  }
  const handlePointerMove = (e: React.PointerEvent) => {
    if (
      Math.abs(e.clientX - start.current.x) > DRAG_THRESHOLD ||
      Math.abs(e.clientY - start.current.y) > DRAG_THRESHOLD
    ) {
      dragged.current = true
    }
  }

  const handleClick = (advisor: Advisor) => {
    if (dragged.current) return
    onClick?.(advisor)
    trackAppyCntEvent(ANALYTICS_EVENTS.CHAT_WITH_ADVISOR, undefined, {
      user: { uid: user?.uid, name: user?.displayName, host: window.location.host },
      advisor: { name: advisor.name, id: advisor.assistantId, chat_type: "Chat with Advisor" },
    })
  }

  // Derive from userUsedCredits (socket) so the banner re-renders as soon as
  // balance arrives — no separate freeMinutesLeft lag / stale null.
  const freeMinutesLeft =
    userUsedCredits !== null ? freeMinutesFromUsedCredits(userUsedCredits) : null
  const showPromo =
    hasStartCreditsLeft(userUsedCredits) &&
    !hasPurchasedCredits &&
    !!freeMinutesLeft

  return (
    <section className="w-full overflow-hidden bg-dark-base pt-4">
      {showPromo && (
        <div className="mb-4 -mt-4 flex h-14 bg-[#80A532]">
          <div
            className="w-14 bg-[length:56px_124px] bg-left bg-no-repeat"
            style={{ backgroundImage: `url(${OfferDecor})` }}
          />
          <div className="flex w-full items-center gap-2 px-2.5 [&_svg]:size-10">
            <DiscIcon />
            <p className="font-body text-base text-[#D8E78C] [&_span]:font-bold [&_span]:text-white">
              {freeMinutesLeft === 1 ? (
                <>
                  <span>Chat for free</span> with any advisor now!
                </>
              ) : (
                <>
                  <span>{freeMinutesLeft} free minutes</span> to chat
                </>
              )}
            </p>
          </div>
          <div
            className="w-14 -scale-x-100 bg-[length:56px_124px] bg-left bg-no-repeat"
            style={{ backgroundImage: `url(${OfferDecor})` }}
          />
        </div>
      )}

      <h2 className="px-4 font-display text-2xl font-medium text-purple-light">
        {sectionCopy.all.title}
      </h2>

      <div
        ref={emblaRef}
        className="mt-2 overflow-visible pb-8"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
      >
        <div className="flex gap-4 pl-4 pt-2">
          {advisorsList.map((advisor, index) => (
            <div key={`${advisor.assistantId}-${index}`} className="flex-[0_0_auto]">
              <button
                type="button"
                onClick={() => handleClick(advisor)}
                className="relative flex size-[92px] items-center justify-center rounded-full bg-gradient-to-br from-peach via-peach to-purple-accent p-1.5"
              >
                <span className="absolute inset-0.5 rounded-full bg-dark-base" />
                <img
                  src={advisor.photo}
                  alt={advisor.name}
                  className="relative size-full rounded-full object-cover object-top"
                />
                <span className="absolute -bottom-3 left-1/2 z-[1] flex -translate-x-1/2 items-center gap-1 text-nowrap rounded-xl border-[3px] border-dark-base bg-green px-2 py-1 font-body text-[10px] text-[#0d081c] [&_svg]:size-1.5">
                  <Dot /> Online
                </span>
                <span className="absolute -bottom-7 left-1/2 -translate-x-1/2 text-nowrap font-body text-sm text-purple-light/70">
                  {advisor.name}
                </span>
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
